import {
  Directive,
  Input,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BehaviorSubject, combineLatest, ReplaySubject, startWith } from "rxjs";
import { SitesService } from "../sites/sites.service";
import { Permission } from "./permissions";
import { UserService } from "./user.service";

@UntilDestroy()
@Directive({
  selector: "[ymsIfHasPermission]",
})
export class IfHasPermissionDirective<
  Operator extends "and" | "or",
  Condition,
> implements OnInit {
  public constructor(
    private readonly user: UserService,
    private readonly template: TemplateRef<Context<Condition>>,
    private readonly viewContainer: ViewContainerRef,
    private readonly sites: SitesService,
  ) {}

  @Input() public set ymsIfHasPermission(value: Permission) {
    this.permissionInput.next(value);
  }
  private readonly permissionInput = new ReplaySubject<Permission>(1);

  @Input() public set ymsIfHasPermissionOperator(value: Operator) {
    this.operatorInput.next(value);
  }
  private readonly operatorInput = new ReplaySubject<Operator>(1);

  @Input() public set ymsIfHasPermissionCondition(value: Condition) {
    this.conditionInput.next(value);
  }
  private readonly conditionInput = new ReplaySubject<Condition>(1);

  @Input() public set ymsIfHasPermissionThen(
    value: TemplateRef<Context<Condition>>,
  ) {
    this.thenTemplateInput.next(value);
  }
  private readonly thenTemplateInput = new BehaviorSubject<TemplateRef<
    Context<Condition>
  > | null>(null);

  @Input() public set ymsIfHasPermissionElse(value: TemplateRef<void>) {
    this.elseTemplateInput.next(value);
  }
  private readonly elseTemplateInput =
    new BehaviorSubject<TemplateRef<void> | null>(null);

  public ngOnInit(): void {
    combineLatest([
      this.permissionInput,
      this.operatorInput.pipe(startWith("and")),
      this.conditionInput.pipe(startWith(initialValueFlag)),
      this.thenTemplateInput,
      this.elseTemplateInput,
      // We need to listen to the selected role changes so we can check on the
      // updated permissions when the user switches sites.
      this.sites.selectedRoleChanges,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([permission, operator, condition, thenTemplate, elseTemplate]) => {
          const andCondition =
            operator !== "and" || condition === initialValueFlag || condition;
          const orCondition =
            operator === "or" && condition !== initialValueFlag && condition;

          this.viewContainer.clear();
          if (
            (this.user.permissions.has(permission) && andCondition) ||
            orCondition
          ) {
            this.viewContainer.createEmbeddedView(
              thenTemplate ?? this.template,
              {
                ymsIfHasPermission: permission,
                ymsIfHasPermissionCondition: condition,
              },
            );
          } else if (elseTemplate) {
            this.viewContainer.createEmbeddedView(elseTemplate);
          }
        },
      );
  }

  /* <Template type guards> */
  /* eslint-disable @typescript-eslint/member-ordering */
  public static ngTemplateGuard_ymsIfHasPermissionCondition<
    Operator extends "and",
    Condition,
  >(
    _directive: IfHasPermissionDirective<Operator, Condition>,
    _expression: Condition,
  ): _expression is Exclude<Condition, false | 0 | "" | null | undefined>;
  public static ngTemplateGuard_ymsIfHasPermissionCondition<
    Operator extends "or",
    Condition,
  >(
    _directive: IfHasPermissionDirective<Operator, Condition>,
    _expression: Condition,
  ): _expression is Condition;
  public static ngTemplateGuard_ymsIfHasPermissionCondition<
    Operator extends "and" | "or",
    Condition,
  >(
    _directive: IfHasPermissionDirective<Operator, Condition>,
    _expression: Condition,
  ): boolean {
    return true;
  }

  public static ngTemplateContextGuard<Condition, Operator extends "and">(
    _directive: IfHasPermissionDirective<Operator, Condition>,
    _context: unknown,
  ): _context is Context<Exclude<Condition, false | 0 | "" | null | undefined>>;
  public static ngTemplateContextGuard<Condition, Operator extends "or">(
    _directive: IfHasPermissionDirective<Operator, Condition>,
    _context: unknown,
  ): _context is Context<Condition>;
  public static ngTemplateContextGuard<
    Condition,
    Operator extends "and" | "or",
  >(
    _directive: IfHasPermissionDirective<Operator, Condition>,
    _context: unknown,
  ): boolean {
    return true;
  }
  /* eslint-enable @typescript-eslint/member-ordering */
  /* </Template type guards> */
}

interface Context<Condition> {
  ymsIfHasPermission: Permission;
  ymsIfHasPermissionCondition: Condition;
}

const initialValueFlag = Symbol("initial condition value");
